import { useState } from "react";
import { Plug, Copy, Check, ChevronDown } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const BASE = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// 外部 App 接入（只读 API）— 参数集中展示 + 令牌管理，管理员在后台直接复制配置。
export function ExternalApiPanel() {
  const { t, lang } = useLanguage();
  const zh = lang === "zh";
  const [copied, setCopied] = useState("");
  const [showEndpoints, setShowEndpoints] = useState(false);

  const copy = (key, value) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    toast.success(t("api.copied"));
    setTimeout(() => setCopied(""), 1500);
  };

  const params = [
    { key: "base", label: zh ? "Base URL（接口根地址）" : "Base URL", value: BASE },
    { key: "header", label: zh ? "认证 Header" : "Auth header", value: "Authorization: Bearer <TOKEN>" },
    { key: "scope", label: zh ? "令牌权限" : "Token scope", value: "readonly" },
    { key: "format", label: zh ? "数据格式" : "Format", value: "JSON / UTF-8" },
  ];

  const curl = `curl -H "Authorization: Bearer <TOKEN>" \\\n  "${BASE}/api/products?page=1&page_size=50"`;

  const endpoints = [
    ["GET /api/products", zh ? "产品列表（page, page_size≤200, search, category, product_type, tag_filter）" : "Product list (page, page_size≤200, search, category, product_type, tag_filter)"],
    ["GET /api/products/{item_number}", zh ? "产品明细 + price_lists[] 尺寸行（成本/箱规/装柜量）" : "Product detail + price_lists[] size rows"],
    ["GET /api/quotes · /api/quotes/{id}", zh ? "报价单列表与明细（items[] 含 price_rows[]）" : "Quote list & detail (items[] with price_rows[])"],
    ["GET /api/procurement-quotes", zh ? "采购报价列表（search, page, page_size）" : "Procurement quotes (search, page, page_size)"],
    ["GET /api/contract-orders", zh ? "合同订单产品（search, contract_no, customer, factory）" : "Contract order products (search, contract_no, customer, factory)"],
    ["GET /api/contract-orders/match?item_number=", zh ? "同款货号的历史合同" : "Historical contracts for one item"],
    ["GET /api/contract-orders/lowest?item_number=", zh ? "历史最低采购价那一笔" : "Lowest historical purchase price"],
    ["GET /api/customers · /api/factories · /api/tags", zh ? "客户 / 工厂 / 标签主数据" : "Customers / factories / tags"],
    ["GET /api/market/products · /api/market/surveys", zh ? "市场调查产品与调查表" : "Market survey products & surveys"],
    ["GET /api/product-overview", zh ? "全部产品照片一览" : "All product photos"],
    ["GET /api/uploads/{file}", zh ? "图片（302 重定向到云存储，需允许跟随重定向；变体 _sm/_md/_lg/_mail）" : "Images (302 → cloud storage; variants _sm/_md/_lg/_mail)"],
  ];

  return (
    <div className="bg-card border border-border rounded-md p-6 space-y-4 mb-5" data-testid="external-api-panel">
      <h3 className="font-heading text-base font-bold tracking-tight flex items-center gap-2">
        <Plug size={16} className="text-brand" /> {t("api.panelTitle")}
      </h3>
      <p className="text-xs text-muted-foreground -mt-2">{t("api.panelHint")}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {params.map((p) => (
          <div key={p.key} className="rounded-md border border-border bg-secondary/25 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{p.label}</p>
            <div className="flex items-center gap-2">
              <code className="text-xs font-mono truncate flex-1" data-testid={`api-param-${p.key}`}>{p.value}</code>
              <button type="button" onClick={() => copy(p.key, p.value)}
                className="text-muted-foreground hover:text-brand shrink-0"
                data-testid={`api-copy-${p.key}`}>
                {copied === p.key ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-md bg-foreground/95 p-3">
        <div className="flex items-start gap-2">
          <pre className="text-[11px] leading-relaxed font-mono text-emerald-300 whitespace-pre-wrap flex-1"
            data-testid="api-curl-sample">{curl}</pre>
          <button type="button" onClick={() => copy("curl", curl)} className="text-white/60 hover:text-white shrink-0"
            data-testid="api-copy-curl">
            {copied === "curl" ? <Check size={13} /> : <Copy size={13} />}
          </button>
        </div>
      </div>

      <div>
        <Button variant="outline" size="sm" className="h-8 text-xs"
          onClick={() => setShowEndpoints((v) => !v)} data-testid="api-toggle-endpoints">
          <ChevronDown size={13} className={showEndpoints ? "rotate-180 transition-transform" : "transition-transform"} />
          {t("api.endpointList")} ({endpoints.length})
        </Button>
        {showEndpoints && (
          <div className="mt-2 rounded-md border border-border divide-y divide-border/60" data-testid="api-endpoint-list">
            {endpoints.map(([ep, desc]) => (
              <div key={ep} className="px-3 py-1.5">
                <code className="text-[11px] font-mono text-brand">{ep}</code>
                <p className="text-[11px] text-muted-foreground">{desc}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <ul className="text-[11px] text-muted-foreground space-y-1 list-disc pl-4">
        <li>{t("api.noteReadonly")}</li>
        <li>{t("api.noteRole")}</li>
        <li>{t("api.noteSecret")}</li>
      </ul>
    </div>
  );
}
